import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { Animated, Pressable, View } from 'react-native';
import { BLUE_100, BLUE_200, WHITE } from '../../constants';
import { useBreakpoint } from '../../hooks';
import { CancelButtonIcon } from '../../icons';
import { StateConfigContext } from '../../providers';
import { announceAccessibility, checkIsPlatform, getTestId } from '../../utils';
import { DynamicSvg } from '../dynamic-svg';
import { Typography } from '../typography';
import { getTooltipProps, getTooltipStyles } from './TooltipStyles';
export var Tooltip = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var tooltipProps = getTooltipProps(props, stateContext);
  var opacityValue = useRef(new Animated.Value(0)).current;
  var _useState = useState({
      children: null,
      tooltip: null
    }),
    layouts = _useState[0],
    setLayouts = _useState[1];
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var tooltipStyles = getTooltipStyles(tooltipProps, layouts, isLargeScreen);
  var children = tooltipProps.children,
    text = tooltipProps.text,
    isVisible = tooltipProps.isVisible,
    showCross = tooltipProps.showCross,
    onClose = tooltipProps.onClose,
    style = tooltipProps.style,
    title = tooltipProps.title,
    ctaText = tooltipProps.ctaText,
    counterText = tooltipProps.counterText,
    secondaryCtaText = tooltipProps.secondaryCtaText,
    onSecondaryCtaPress = tooltipProps.onSecondaryCtaPress,
    onCtaPress = tooltipProps.onCtaPress,
    autoHide = tooltipProps.autoHide,
    autoHideTimeout = tooltipProps.autoHideTimeout,
    iconName = tooltipProps.iconName,
    iconProps = tooltipProps.iconProps,
    _tooltipProps$testID = tooltipProps.testID,
    testID = _tooltipProps$testID === void 0 ? 'Tooltip' : _tooltipProps$testID;
  var _useState2 = useState(isVisible),
    showTooltip = _useState2[0],
    setShowTooltip = _useState2[1];
  var timeoutRef = useRef(null);
  useEffect(() => {
    setShowTooltip(isVisible);
  }, [isVisible]);
  useEffect(() => {
    if (showTooltip && autoHide && autoHideTimeout) {
      timeoutRef.current = setTimeout(() => {
        closeToolTip();
        onClose === null || onClose === void 0 ? void 0 : onClose({
          onCrossClicked: false
        });
      }, autoHideTimeout);
    }
    Animated.timing(opacityValue, {
      toValue: showTooltip ? 1 : 0,
      duration: 240,
      useNativeDriver: false
    }).start(() => {
      if (showTooltip) {
        var textsToAnnounce = [text];
        announceAccessibility(textsToAnnounce);
      }
    });
    return () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [showTooltip]);
  var closeToolTip = () => {
    setShowTooltip(false);
    timeoutRef.current && clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  };
  var updateLayoutState = (key, event) => {
    if (typeof (event === null || event === void 0 ? void 0 : event.persist) === 'function' && !checkIsPlatform('web')) {
      event === null || event === void 0 ? void 0 : event.persist();
    }
    setLayouts(prevState => {
      var _a;
      return Object.assign(Object.assign({}, prevState), {
        [key]: (_a = event === null || event === void 0 ? void 0 : event.nativeEvent) === null || _a === void 0 ? void 0 : _a.layout
      });
    });
  };
  return _jsxs(View, Object.assign({
    ref: ref
  }, showTooltip && {
    role: 'tooltip'
  }, {
    style: [showTooltip && tooltipStyles.container, style],
    onLayout: event => {
      updateLayoutState('children', event);
    }
  }, getTestId(testID), {
    children: [_jsx(View, Object.assign({
      style: showTooltip && tooltipStyles.children
    }, {
      children: children
    })), showTooltip && _jsx(Animated.View, Object.assign({
      style: [tooltipStyles.tooltipContainer, {
        opacity: opacityValue
      }],
      onLayout: event => {
        updateLayoutState('tooltipContainer', event);
      }
    }, checkIsPlatform('web') && {
      role: 'alert'
    }, {
      children: _jsxs(View, Object.assign({
        style: tooltipStyles.tooltipContent
      }, {
        children: [_jsx(View, {
          style: tooltipStyles.carrot
        }), _jsxs(View, Object.assign({
          style: tooltipStyles.tooltipBody,
          onLayout: event => {
            updateLayoutState('tooltip', event);
          }
        }, {
          children: [!!iconName && _jsx(DynamicSvg, Object.assign({
            iconName: iconName,
            color: WHITE
          }, iconProps, getTestId(testID, iconName), {
            style: [tooltipStyles.icon, iconProps === null || iconProps === void 0 ? void 0 : iconProps.style]
          })), _jsxs(View, Object.assign({
            style: tooltipStyles.content
          }, {
            children: [_jsxs(View, Object.assign({
              style: tooltipStyles.descriptionContainer
            }, {
              children: [_jsxs(View, Object.assign({
                style: tooltipStyles.descriptionContent
              }, {
                children: [_jsx(Typography, Object.assign({
                  text: title,
                  type: "body-para-xs-bold",
                  color: WHITE,
                  numberOfLines: 1
                }, getTestId(testID, 'title'))), _jsx(Typography, Object.assign({
                  text: text,
                  type: "body-para-xs",
                  color: BLUE_100,
                  numberOfLines: 2
                }, getTestId(testID, 'text')))]
              })), showCross && _jsx(Pressable, Object.assign({
                accessibilityRole: "button",
                onPress: () => {
                  closeToolTip();
                  onClose === null || onClose === void 0 ? void 0 : onClose({
                    onCrossClicked: true
                  });
                },
                style: tooltipStyles.cross
              }, getTestId(testID, 'pressable'), {
                children: _jsx(CancelButtonIcon, Object.assign({
                  fontSize: 16,
                  color: BLUE_200
                }, getTestId(testID, 'showCross')))
              }))]
            })), _jsxs(View, Object.assign({
              style: tooltipStyles.stepCtaContainer
            }, {
              children: [!!counterText && _jsx(Typography, Object.assign({
                style: tooltipStyles.counterText,
                text: counterText,
                type: "body-single-line-xxs-bold",
                color: BLUE_200
              }, getTestId(testID, 'counterText'))), _jsxs(View, Object.assign({
                style: tooltipStyles.ctaContainer
              }, {
                children: [!!secondaryCtaText && _jsx(Pressable, Object.assign({
                  style: tooltipStyles.ctaStyles,
                  onPress: onSecondaryCtaPress
                }, {
                  children: _jsx(Typography, Object.assign({
                    text: secondaryCtaText,
                    type: "body-hyperlink-xxs",
                    color: BLUE_100
                  }, getTestId(testID, 'secondaryCta')))
                })), !!ctaText && _jsx(Pressable, Object.assign({
                  style: [tooltipStyles.ctaStyles, tooltipStyles.primaryCtaStyle],
                  onPress: onCtaPress
                }, {
                  children: _jsx(Typography, Object.assign({
                    text: ctaText,
                    type: "title-single-line-sm",
                    color: WHITE
                  }, getTestId(testID, 'cta')))
                }))]
              }))]
            }))]
          }))]
        }))]
      }))
    }))]
  }));
});